import { z } from "zod";
import { parseRedirectTarget } from "@/common/utils/parseRedirectTarget";

export enum ProviderSignInIntent {
  Jobs = "jobs",
}

export const providerSignInSearchSchema = z.object({
  l: z.string().optional(),
  redirect: z.string().regex(/^\//).optional(),
  intent: z.nativeEnum(ProviderSignInIntent).optional().catch(undefined),
});

export type ProviderSignInSearch = z.infer<typeof providerSignInSearchSchema>;

const PROVIDER_INVITE_PATH = "/provider-invite";
const JOB_BROWSING_PATHS = new Set([
  "/provider/jobs/search",
  "/provider/jobs/recommended",
  "/provider/jobs/interests",
]);
const JOB_DETAIL_PATH_PREFIX = "/provider/jobs/";
const jobIdSchema = z.string().uuid();

export function getProviderSignInIntent({
  intent,
  redirect,
}: ProviderSignInSearch): ProviderSignInIntent | undefined {
  let pathname: string | undefined;
  if (redirect) {
    try {
      pathname = decodeURIComponent(parseRedirectTarget(redirect).to).replace(
        /\/+$/,
        "",
      );
    } catch {
      return undefined;
    }
  }

  // An invitation always keeps organization messaging, even if a shared URL
  // also carries a jobs intent. This choice only affects the sign-in pitch.
  if (pathname === PROVIDER_INVITE_PATH) {
    return undefined;
  }
  if (intent === ProviderSignInIntent.Jobs) {
    return intent;
  }
  if (!pathname) {
    return undefined;
  }
  if (JOB_BROWSING_PATHS.has(pathname)) {
    return ProviderSignInIntent.Jobs;
  }

  // Assigned work, offers, and timesheets share this route family with the
  // marketplace, so only an actual job-post ID indicates job-seeking intent.
  if (
    pathname.startsWith(JOB_DETAIL_PATH_PREFIX) &&
    jobIdSchema.safeParse(pathname.slice(JOB_DETAIL_PATH_PREFIX.length)).success
  ) {
    return ProviderSignInIntent.Jobs;
  }
  return undefined;
}
